import WorkHistoryOutlined from "@mui/icons-material/WorkHistoryOutlined";
import type { TFunction } from "i18next";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useJobs } from "../../hooks/useJobs";
import type { BulkNurseJobsPreview, TeamNurse } from "../../utils/models";
import { SystemWizardDialog, type WizardStep } from "../UIToolkit/Popups/SystemWizardDialog";
import { BulkJobChooseStep } from "./BulkJobChooseStep";
import { BulkJobReviewStep } from "./BulkJobReviewStep";
import { type BulkJobStepId, countWillChange, reviewPrimaryKey } from "./bulkJobChange";
import { useBulkJobChangeFlow } from "./useBulkJobChangeFlow";

type Props = {
	/** The Team page selection this change applies to — read once, when the dialog opens. */
	nurses: TeamNurse[];
	/** Cancel / close — the selection stays. */
	onClose: () => void;
	/** The change was written. Receives the ids that could not be updated, so the page can keep them selected. */
	onApplied: (failedNurseIds: number[]) => void;
};

type Flow = ReturnType<typeof useBulkJobChangeFlow>;

/**
 * Bulk "Change job" for the Team page selection: choose (add a job, or replace one with another), then
 * review exactly who changes before anything is written. The review step is the bulk form of the
 * single-employee "Apply shifts to new job" pop-up — it lists the people with future shifts under the
 * replaced job and lets the admin decide for all of them at once.
 */
export const BulkJobChangeDialog: FC<Props> = ({ nurses, onClose, onApplied }) => {
	const { t } = useTranslation();
	const enabledJobs = useJobs();
	const flow = useBulkJobChangeFlow(nurses, onClose, onApplied);

	const steps: WizardStep<BulkJobStepId>[] = [
		{
			id: "choose",
			label: t("team.bulk.jobs.step.choose"),
			content: <BulkJobChooseStep picks={flow.picks} onPicksChange={flow.setPicks} enabledJobs={enabledJobs} heldJobs={flow.heldJobs} />,
			primaryLabel: t("team.bulk.jobs.continue"),
			onPrimary: flow.handleContinue,
			primaryDisabled: !flow.request || flow.preview.isPending,
			primaryLoading: flow.preview.isPending,
			primaryTooltip: flow.request ? undefined : t(flow.picks.action === "add" ? "team.bulk.jobs.continue.need_add" : "team.bulk.jobs.continue.need_replace"),
		},
		reviewStep(t, flow),
	];

	return (
		<SystemWizardDialog<BulkJobStepId>
			open
			onClose={flow.handleClose}
			icon={<WorkHistoryOutlined />}
			title={t("team.bulk.jobs.title")}
			subtitle={t("team.bulk.jobs.subtitle", { count: flow.nurses.length })}
			steps={steps}
			activeStepId={flow.stepId}
			onStepChange={flow.setStepId}
			backLabel={t("back")}
			closeLabel={t("cancel")}
			railAriaLabel={t("team.bulk.jobs.rail")}
			maxWidth="sm"
			dataTestId="team-bulk-jobs-dialog"
		/>
	);
};

/** Why the apply button is off, naming the one thing that would turn it on. */
const disabledReason = (t: TFunction, flow: Flow, preview: BulkNurseJobsPreview, nothingToChange: boolean): string | undefined => {
	if (!nothingToChange) return undefined;
	// Nothing changes yet some people are blocked → they are all being left alone; moving their shifts is the way out.
	return preview.blockedByShifts.length > 0
		? t("team.bulk.jobs.apply.disabled.all_blocked", { from: flow.fromJob?.displayName, to: flow.toJob?.displayName })
		: t("team.bulk.jobs.apply.disabled.none");
};

/**
 * The review step. Only reachable once the preview has resolved (Continue advances on its success), so
 * without one the step isn't on screen and renders nothing.
 */
const reviewStep = (t: TFunction, flow: Flow): WizardStep<BulkJobStepId> => {
	const { data: preview } = flow.preview;
	const base = { id: "review" as const, label: t("team.bulk.jobs.step.review"), onBack: flow.handleBack, onPrimary: flow.handleApply };
	if (!preview || !flow.toJob) return { ...base, content: null, primaryLabel: t("team.bulk.jobs.confirm.add"), primaryDisabled: true };

	const nothingToChange = countWillChange(preview, flow.blockedMode) === 0;
	return {
		...base,
		content: (
			<BulkJobReviewStep
				action={flow.picks.action}
				fromJob={flow.fromJob}
				toJob={flow.toJob}
				preview={preview}
				nameById={flow.nameById}
				blockedMode={flow.blockedMode}
				onBlockedModeChange={flow.setBlockedMode}
			/>
		),
		primaryLabel: t(reviewPrimaryKey(flow.picks.action, preview, flow.blockedMode)),
		primaryDisabled: nothingToChange || flow.isApplying,
		primaryLoading: flow.isApplying,
		primaryTooltip: disabledReason(t, flow, preview, nothingToChange),
		// While the batch is on the wire there is nothing to go back to, and Cancel would do nothing.
		hideBack: flow.isApplying,
		hideClose: flow.isApplying,
	};
};
